import { Component, OnInit, ViewChild, inject, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Table, TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ConfirmPopupModule } from 'primeng/confirmpopup';
import { ConfirmationService, MessageService } from 'primeng/api';
import { AncillaryItem, UpdateAncillaryStatusRequest } from '@app/shared/interfaces/ancillary.interface';
import { AncillaryService } from '@app/shared/services/ancillary.service';
import { DialogModule } from 'primeng/dialog';
import {
    AncillaryCreateEditComponent,
    AncillaryDialogMode,
    AncillaryFormModel
} from './ancillary-create-edit/ancillary-create-edit.component';
import { ToastModule } from 'primeng/toast';
import { SelectModule } from 'primeng/select';
import { FormsModule } from '@angular/forms';
import { SelectItem } from 'primeng/api';
import { ColumnFilterComponent } from '@app/shared/components/column-filter/column-filter.component';
import { PageHeaderComponent } from '@app/shared/components/page-header/page-header.component';

@Component({
    selector: 'app-ancillary-management',
    imports: [
        CommonModule,
        TableModule,
        ButtonModule,
        ProgressSpinnerModule,
        ConfirmPopupModule,
        DialogModule,
        ToastModule,
        AncillaryCreateEditComponent,
        SelectModule,
        FormsModule,
        ColumnFilterComponent,
        PageHeaderComponent
    ],
    templateUrl: './ancillary-management.component.html',
    styleUrl: './ancillary-management.component.scss'
})
export class AncillaryManagementComponent implements OnInit {
    @ViewChild('dt') dt!: Table;

    ancillaries: AncillaryItem[] = [];
    loading = true;
    statusOptions: SelectItem[] = [
        { label: 'Active', value: 'ACTIVE' },
        { label: 'Forced', value: 'FORCED' },
        { label: 'Passive', value: 'PASSIVE' }
    ];

    ancillaryService = inject(AncillaryService);
    private confirmationService = inject(ConfirmationService);
    private messageService = inject(MessageService);

    displayDialog = false;
    dialogMode: AncillaryDialogMode = 'add';
    dialogHeader = '';
    selected: AncillaryItem | null = null;
    dialogData: Partial<AncillaryFormModel> | null = null;

    private cd = inject(ChangeDetectorRef);

    ngOnInit(): void {
        this.loadAncillaries();
    }

    loadAncillaries(): void {
        this.loading = true;
        this.cd.detectChanges();
        this.ancillaryService.getAncillaries().subscribe({
            next: (data) => {
                this.ancillaries = data.map((item) => ({
                    ...item,
                    uiDisplayText: item.titlesByLanguage ? Object.values(item.titlesByLanguage).filter(Boolean).join(' ') : ''
                }));
                this.loading = false;
                this.cd.detectChanges();
            },
            error: () => {
                this.loading = false;
                this.cd.detectChanges();
            }
        });
    }

    onEdit(row: AncillaryItem): void {
        this.dialogMode = 'edit';
        this.dialogHeader = 'Edit';
        this.selected = row;
        this.dialogData = this.mapToDialogData(row);
        this.displayDialog = true;
    }

    openAdd(): void {
        this.dialogMode = 'add';
        this.dialogHeader = 'Add New';
        this.selected = null;
        this.dialogData = null;
        this.displayDialog = true;
    }

    confirmDelete(event: Event, row: AncillaryItem): void {
        this.confirmationService.confirm({
            target: event.target as HTMLElement,
            message: 'Are you sure you want to delete this item?',
            icon: 'pi pi-exclamation-triangle',
            acceptButtonProps: { severity: 'danger' },
            accept: () => this.onDelete(row)
        });
    }

    onDelete(row: AncillaryItem): void {
        if (!row?.id) return;
        this.ancillaryService.deleteAncillary(row.id).subscribe({
            next: () => {
                this.messageService.add({ severity: 'success', summary: 'Success', detail: 'The item has been deleted successfully.' });
                this.loadAncillaries();
            }
        });
    }

    onDialogCancel(): void {
        this.displayDialog = false;
    }

    onDialogSave(model: AncillaryFormModel): void {
        const payload = {
            id: model.id ?? 0,
            ancillaryKey: model.ancillaryKey,
            status: model.status,
            providerCode: model.providerCode as string,
            fareTypeName: model.fareTypeName as string,
            categoryName: model.categoryName as string,
            mandatorySelection: model.mandatorySelection,
            titlesByLanguage: model.titlesByLanguage
        } as any;

        if (this.dialogMode === 'add') {
            this.ancillaryService.createAncillary(payload).subscribe({
                next: () => {
                    this.messageService.add({ severity: 'success', summary: 'Success', detail: 'The item has been created successfully.' });
                    this.displayDialog = false;
                    this.loadAncillaries();
                }
            });
        } else if (this.dialogMode === 'edit' && this.selected?.id) {
            this.ancillaryService.updateAncillary(this.selected.id, payload).subscribe({
                next: () => {
                    this.messageService.add({ severity: 'success', summary: 'Success', detail: 'The item has been updated successfully.' });
                    this.displayDialog = false;
                    this.loadAncillaries();
                }
            });
        }
    }

    onStatusChange(event: any, row: AncillaryItem): void {
        if (!row?.id) return;

        const request: UpdateAncillaryStatusRequest = {
            status: event.value
        };

        this.ancillaryService.updateAncillaryStatus(row.id, request).subscribe({
            next: () => {
                this.messageService.add({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Status has been updated successfully.'
                });
                this.loadAncillaries();
            },
            error: () => {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Failed to update status.'
                });
                this.loadAncillaries();
            }
        });
    }

    private mapToDialogData(row: AncillaryItem): Partial<AncillaryFormModel> {
        return {
            id: row.id,
            ancillaryKey: row.ancillaryKey,
            providerCode: row.providerCode,
            fareTypeName: row.fareTypeName,
            categoryName: row.categoryName,
            mandatorySelection: row.mandatorySelection,
            status: row.status as any,
            titlesByLanguage: {
                ['TR']: row.titlesByLanguage?.['TR'] ?? '',
                ['NL']: row.titlesByLanguage?.['NL'] ?? '',
                ['DE']: row.titlesByLanguage?.['DE'] ?? '',
                ['EN']: row.titlesByLanguage?.['EN'] ?? ''
            }
        } as Partial<AncillaryFormModel>;
    }
}
